import { useMemo, useCallback } from 'react'
import { TFunction } from 'i18next'
import { useSelector } from 'react-redux'
import { useLocation, useHistory } from 'react-router-dom'
import { useNewTranslation } from 'hooks'
import { fieldIdentifiers } from 'components/Shared/Comment'
import { PayoutState, EngagementId, EngagementIds, TaskState, StepState } from 'state/tasks'
import { camelCaseToSnakeCase } from 'components/utils'
import { selectEditingCampaign } from 'state/campaigns'
import { selectCampaignComments, ThreadCommentState } from 'state/comments'
import { selectRegistrationForm } from 'state/registrations'
import { getSectionComments } from 'components/utils/comments'
import {
    publishIdentifiers,
    tasksIdentifiers,
    registrationIdentifiers,
    templateSelectorIdentifiers,
    templateDetailIdentifiers,
} from 'components/Shared/Comment/constant'
import { selectCityTypesByEditingCampaignCountry } from 'state/constants'
import { useLocationTranslation } from 'hooks'
import { selectTasks } from 'state/tasks'
import { TaskSections } from 'components/Campaign/Editor/TaskEditor'
import { CampaignTemplateStep, CampaignTemplateStepValues, selectCampaignTasks } from 'state/campaignTemplates'

const getCardTitle = (identifier: string, t: TFunction) => {
    const titlesList = {
        [fieldIdentifiers.brandName]: t('manageTeams.teamPage.brandNameTableLabel'),
        [fieldIdentifiers.campaignName]: t('campaignEditor.setUpCampaign.enterCampaignNameHeader'),
        [fieldIdentifiers.objective]: t('campaignEditor.setUpCampaign.enterCampaignGoalHeader'),
        [fieldIdentifiers.categories]: t('campaignEditor.setUpCampaign.enterCampaignCategoryHeader'),
        [fieldIdentifiers.budget]: t('campaignEditor.setUpCampaign.enterCampaignBudgetHeader'),
        [fieldIdentifiers.slotNumber]: t('campaign.metric.enterCampaignSlotsHeader'),
        [fieldIdentifiers.isPrivate]: t('card.allCampaigns.privateCampaignLabel'),
        [fieldIdentifiers.registrationEnable]: t('campaignEditor.setUpCampaign.turnOnRegistrationHeader'),
        [fieldIdentifiers.registrationMaxSlots]: t('campaignEditor.setUpCampaign.enterRegistrationSlotsHeader'),
        [fieldIdentifiers.profileRecommendationEnabled]: t('campaignEditor.setUpCampaign.turnOnSmartProfileTitle'),
        [fieldIdentifiers.autoUpdateSubmissionStatus]: t('campaignEditor.setUpCampaign.turnOnAutoStatusHeader'),
        [fieldIdentifiers.submissionStatusFullMin]: t('campaign.metric.enterNumberSubmissionFullHeader'),
        [fieldIdentifiers.submissionStatusFillingFastMin]: t('campaign.metric.enterNumberSubmissionFillingFastHeader'),
        [fieldIdentifiers.batchEnable]: t('campaignEditor.setUpCampaign.turnOnBatchSubmissionHeader'),
        [fieldIdentifiers.campaignBanner]: t('campaignEditor.completeSetup.uploadCampaignBannerHeader'),
        [fieldIdentifiers.campaignDescription]: t('campaignEditor.completeSetup.enterCampaignDescriptionHeader'),
        [fieldIdentifiers.payoutDeadline]: t('campaignEditor.completeSetup.enterPayoutDeadlineHeader'),
        [fieldIdentifiers.setupTitle]: t('campaignEditor.createTask.enterTitleHeader'),
        [fieldIdentifiers.setupDescription]: t('campaignEditor.createTask.enterDescriptionHeader'),
        [fieldIdentifiers.setupImages]: t('campaignEditor.createTask.uploadFilesExampleHeader'),
        [fieldIdentifiers.start]: t('general.information.startDate'),
        [fieldIdentifiers.end]: t('general.information.endDate'),
        [fieldIdentifiers.invitationExpiry]: t('campaignEditor.createTask.enterInvitationExpiryHeader'),
        [fieldIdentifiers.stepTitle]: t('campaignEditor.createTask.enterStepTitleHeader'),
        [fieldIdentifiers.stepDescription]: t('campaignEditor.createTask.enterDescriptionHeader'),
        [fieldIdentifiers.photoRequirementDos]: t('campaignEditor.createTask.enterRequirementDoHeader'),
        [fieldIdentifiers.photoRequirementDonts]: t('campaignEditor.createTask.enterRequirementDontHeader'),
        [fieldIdentifiers.examplePhotos]: t('campaignEditor.createTask.uploadFilesExampleHeader'),
        [fieldIdentifiers.examplePhotosDescription]: t('campaignEditor.createTask.enterDescriptionHeader'),
        [fieldIdentifiers.captionRequirementDos]: t('campaignEditor.createTask.enterRequirementDoHeader'),
        [fieldIdentifiers.captionRequirementDonts]: t('campaignEditor.createTask.enterRequirementDontHeader'),
        [fieldIdentifiers.captionRequirementHashtags]: t('campaignEditor.createTask.addHashtagHeader'),
        [fieldIdentifiers.profileTags]: t('campaignEditor.createTask.addRequiredProfileTagHeader'),
        [fieldIdentifiers.videoCodeToggle]: t('submission.table.videoCodeLabel'),
        [fieldIdentifiers.linkInBioToggle]: t('campaignEditor.createTask.turnOnLinkInBioHeader'),
        [fieldIdentifiers.engagementMetrics]: t('campaignEditor.createTask.selectEngagementMetricsSubheader'),
        [fieldIdentifiers.schedulePostToggle]: t('campaignEditor.createTask.turnOnScheduledPostHeader'),
        [fieldIdentifiers.payoutBase]: t('campaignEditor.createTask.enterFixedPayoutHeader'),
        [fieldIdentifiers.extraPayoutTitle]: t('campaignEditor.createTask.enterTitleHeader'),
        [fieldIdentifiers.extraPayoutDescription]: t('campaignEditor.createTask.enterDescriptionHeader'),
        [fieldIdentifiers.extraPayoutCost]: t('campaignEditor.createTask.enterPayoutForEachHeader'),
        [fieldIdentifiers.extraPayoutMin]: t('campaignEditor.createTask.enterMinimumOtherPayout'),
        [fieldIdentifiers.extraPayoutMax]: t('campaignEditor.createTask.enterMaximumOtherPayout'),
        [fieldIdentifiers.productName]: t('campaignEditor.createTask.productNameHeader'),
        [fieldIdentifiers.productDescription]: t('campaignEditor.createTask.productDescriptionHeader'),
        [fieldIdentifiers.howToBuyTitle]: t('campaignEditor.createTask.enterInstructionsHeader'),
        [fieldIdentifiers.productPhotos]: t('campaignEditor.createTask.productPhotosHeader'),
        [fieldIdentifiers.photosDescription]: t('campaignEditor.createTask.productDescriptionHeader'),
        [fieldIdentifiers.purchaseProductToggle]: t('campaignEditor.createTask.turnOnProofPurchaseHeader'),
        [fieldIdentifiers.attendEventToggle]: t('campaignEditor.createTask.turnOnProofAttendanceHeader'),
        [fieldIdentifiers.thingsToNote]: t('campaignEditor.createTask.enterInstructionsHeader'),
        [fieldIdentifiers.eventName]: t('campaignEditor.createTask.enterEventNameHeader'),
        [fieldIdentifiers.eventDescription]: t('campaignEditor.createTask.enterEventDescriptionHeader'),
        [fieldIdentifiers.eventInformation]: t('campaignEditor.createTask.enterEventInformationHeader'),
        [fieldIdentifiers.setupTaskType]: t('campaignEditor.customAndRegistrationField.taskTypeHeader'),
        [fieldIdentifiers.blockTitle]: t('campaignEditor.createTask.enterTitleHeader'),
        [fieldIdentifiers.blockDescription]: t('campaignEditor.createTask.enterDescriptionHeader'),
        [fieldIdentifiers.blockImages]: t('campaignEditor.customAndRegistrationField.uploadPhotoHeader'),
        [fieldIdentifiers.blockIsRequired]: t('campaignEditor.customAndRegistrationField.turnOnRequiredQuestionHeader'),
        [fieldIdentifiers.numberOfPhotos]: t('campaignEditor.customAndRegistrationField.enterMaximumPhotosHeader'),
        [fieldIdentifiers.blockIsMultiAnswer]: t(
            'campaignEditor.customAndRegistrationField.turnOnMultipleSelectionHeader',
        ),
        [fieldIdentifiers.blockQuestionNumMin]: t('campaignEditor.customAndRegistrationField.enterMinimumNumberHeader'),
        [fieldIdentifiers.blockQuestionNumMax]: t('campaignEditor.customAndRegistrationField.enterMaximumNumberHeader'),
        [fieldIdentifiers.blockQuestionTitle]: t('campaignEditor.customAndRegistrationField.enterQuestionHeader'),
        [fieldIdentifiers.options]: t('campaignEditor.customAndRegistrationField.optionTitle'),
        [fieldIdentifiers.blockIsLongAnswer]: t('campaignEditor.customAndRegistrationField.turnOnParagraphFormHeader'),
        [fieldIdentifiers.ageMin]: t('campaignEditor.requirement.enterMinimumAge'),
        [fieldIdentifiers.ageMax]: t('campaignEditor.requirement.enterMaximumAge'),
        [fieldIdentifiers.gender]: t('campaignEditor.requirement.genderHeader'),
        [fieldIdentifiers.occupation]: t('campaignEditor.requirement.occupationHeader'),
        [fieldIdentifiers.conflictInput]: t('campaignEditor.requirement.conflictingCampaignsHeader'),
        [fieldIdentifiers.conflictTitle]: t('campaignEditor.requirement.conflictingCampaignListHeader'),
        [fieldIdentifiers.profileTypes]: t('campaignEditor.requirement.typeHeader'),
        [fieldIdentifiers.freeInput]: t('campaignEditor.requirement.freeInputHeader'),
        [fieldIdentifiers.min]: t('campaignEditor.requirement.enterInstagramMinimumFollower'),
        [fieldIdentifiers.max]: t('campaignEditor.requirement.enterInstagramMaximumFollower'),
        [fieldIdentifiers.campaignRequirement]: t('campaignEditor.completeSetup.enterCampaignRequirementHeader'),
        [fieldIdentifiers.registrationFieldsHeader]: t('campaignEditor.createTask.selectRegistrationFieldsHeader'),
        [fieldIdentifiers.rank1]: getLocationTitle().rank1Title,
        [fieldIdentifiers.rank2]: getLocationTitle().rank2Title,
        [fieldIdentifiers.additionTitle]: t('campaignEditor.createTask.enterTitleHeader'),

        //engagement
        //view
        [`${EngagementId.VIEW}_${fieldIdentifiers.engagementToggle}`]: t('engagementMetric.view'),
        [`${EngagementId.VIEW}_${fieldIdentifiers.engagementCost}`]: t('engagementMetric.enterPayoutPerView'),
        [`${EngagementId.VIEW}_${fieldIdentifiers.engagementMin}`]: t('engagementMetric.enterMinimumView'),
        [`${EngagementId.VIEW}_${fieldIdentifiers.engagementMax}`]: t('engagementMetric.enterMaximumView'),
        //like
        [`${EngagementId.LIKE}_${fieldIdentifiers.engagementToggle}`]: t('engagementMetric.like'),
        [`${EngagementId.LIKE}_${fieldIdentifiers.engagementCost}`]: t('engagementMetric.enterPayoutPerLike'),
        [`${EngagementId.LIKE}_${fieldIdentifiers.engagementMin}`]: t('engagementMetric.enterMinimumLike'),
        [`${EngagementId.LIKE}_${fieldIdentifiers.engagementMax}`]: t('engagementMetric.enterMaximumLike'),
        //reach
        [`${EngagementId.REACH}_${fieldIdentifiers.engagementToggle}`]: t('engagementMetric.reach'),
        [`${EngagementId.REACH}_${fieldIdentifiers.engagementCost}`]: t('engagementMetric.enterPayoutPerReach'),
        [`${EngagementId.REACH}_${fieldIdentifiers.engagementMin}`]: t('engagementMetric.enterMinimumReach'),
        [`${EngagementId.REACH}_${fieldIdentifiers.engagementMax}`]: t('engagementMetric.enterMaximumReach'),
        //impression
        [`${EngagementId.IMPRESSION}_${fieldIdentifiers.engagementToggle}`]: t('engagementMetric.impression'),
        [`${EngagementId.IMPRESSION}_${fieldIdentifiers.engagementCost}`]: t(
            'engagementMetric.enterPayoutPerImpression',
        ),
        [`${EngagementId.IMPRESSION}_${fieldIdentifiers.engagementMin}`]: t('engagementMetric.enterMinimumImpression'),
        [`${EngagementId.IMPRESSION}_${fieldIdentifiers.engagementMax}`]: t('engagementMetric.enterMaximumImpression'),

        //comment
        [`${EngagementId.COMMENT}_${fieldIdentifiers.engagementToggle}`]: t('engagementMetric.numberComments'),
        [`${EngagementId.COMMENT}_${fieldIdentifiers.engagementCost}`]: t('engagementMetric.enterPayoutPerComment'),
        [`${EngagementId.COMMENT}_${fieldIdentifiers.engagementMin}`]: t('engagementMetric.enterMinimumComment'),
        [`${EngagementId.COMMENT}_${fieldIdentifiers.engagementMax}`]: t('engagementMetric.enterMaximumComment'),

        //tag
        [`${EngagementId.TAG}_${fieldIdentifiers.engagementToggle}`]: t('engagementMetric.tag'),
        [`${EngagementId.TAG}_${fieldIdentifiers.engagementCost}`]: t('engagementMetric.enterPayoutPerTag'),
        [`${EngagementId.TAG}_${fieldIdentifiers.engagementMin}`]: t('engagementMetric.enterMinimumTag'),
        [`${EngagementId.TAG}_${fieldIdentifiers.engagementMax}`]: t('engagementMetric.enterMaximumTag'),

        //share
        [`${EngagementId.SHARE}_${fieldIdentifiers.engagementToggle}`]: t('engagementMetric.share'),
        [`${EngagementId.SHARE}_${fieldIdentifiers.engagementCost}`]: t('engagementMetric.enterPayoutPerShare'),
        [`${EngagementId.SHARE}_${fieldIdentifiers.engagementMin}`]: t('engagementMetric.enterMinimumShare'),
        [`${EngagementId.SHARE}_${fieldIdentifiers.engagementMax}`]: t('engagementMetric.enterMaximumShare'),

        //sticker tap
        [`${EngagementId.STICKER_TAP}_${fieldIdentifiers.engagementToggle}`]: t('engagementMetric.stickerTap'),
        [`${EngagementId.STICKER_TAP}_${fieldIdentifiers.engagementCost}`]: t(
            'engagementMetric.enterPayoutPerStickerTap',
        ),
        [`${EngagementId.STICKER_TAP}_${fieldIdentifiers.engagementMin}`]: t('engagementMetric.enterMinimumStickerTap'),
        [`${EngagementId.STICKER_TAP}_${fieldIdentifiers.engagementMax}`]: t('engagementMetric.enterMaximumStickerTap'),

        //link click
        [`${EngagementId.LINK_CLICK}_${fieldIdentifiers.engagementToggle}`]: t('engagementMetric.linkClick'),
        [`${EngagementId.LINK_CLICK}_${fieldIdentifiers.engagementCost}`]: t(
            'engagementMetric.enterPayoutPerLinkClick',
        ),
        [`${EngagementId.LINK_CLICK}_${fieldIdentifiers.engagementMin}`]: t('engagementMetric.enterMinimumLinkClick'),
        [`${EngagementId.LINK_CLICK}_${fieldIdentifiers.engagementMax}`]: t('engagementMetric.enterMaximumLinkClick'),

        // cta click
        [`${EngagementId.CTA_CLICK}_${fieldIdentifiers.engagementToggle}`]: t(
            'engagementMetric.ctaButtonClicksBoostedPosts',
        ),
        [`${EngagementId.CTA_CLICK}_${fieldIdentifiers.engagementCost}`]: t(
            'engagementMetric.enterPayoutPerCtaButtonClick',
        ),
        [`${EngagementId.CTA_CLICK}_${fieldIdentifiers.engagementMin}`]: t(
            'engagementMetric.enterMinimumCtaButtonClick',
        ),
        [`${EngagementId.CTA_CLICK}_${fieldIdentifiers.engagementMax}`]: t(
            'engagementMetric.enterMaximumCtaButtonClick',
        ),
        //save
        [`${EngagementId.SAVE}_${fieldIdentifiers.engagementToggle}`]: t('engagementMetric.save'),
        [`${EngagementId.SAVE}_${fieldIdentifiers.engagementCost}`]: t('engagementMetric.enterPayoutPerSave'),
        [`${EngagementId.SAVE}_${fieldIdentifiers.engagementMin}`]: t('engagementMetric.enterMinimumSave'),
        [`${EngagementId.SAVE}_${fieldIdentifiers.engagementMax}`]: t('engagementMetric.enterMaximumSave'),

        // reply
        [`${EngagementId.REPLY}_${fieldIdentifiers.engagementToggle}`]: t('engagementMetric.reply'),
        [`${EngagementId.REPLY}_${fieldIdentifiers.engagementCost}`]: t('engagementMetric.enterPayoutPerReply'),
        [`${EngagementId.REPLY}_${fieldIdentifiers.engagementMin}`]: t('engagementMetric.enterMinimumReply'),
        [`${EngagementId.REPLY}_${fieldIdentifiers.engagementMax}`]: t('engagementMetric.enterMaximumReply'),

        //retweet
        [`${EngagementId.RETWEET}_${fieldIdentifiers.engagementToggle}`]: t('engagementMetric.retweet'),
        [`${EngagementId.RETWEET}_${fieldIdentifiers.engagementCost}`]: t('engagementMetric.enterPayoutPerRetweet'),
        [`${EngagementId.RETWEET}_${fieldIdentifiers.engagementMin}`]: t('engagementMetric.enterMinimumRetweet'),
        [`${EngagementId.RETWEET}_${fieldIdentifiers.engagementMax}`]: t('engagementMetric.enterMaximumRetweet'),

        //engagement
        [`${EngagementId.ENGAGEMENT}_${fieldIdentifiers.engagementToggle}`]: t('engagementMetric.engagement'),
        [`${EngagementId.ENGAGEMENT}_${fieldIdentifiers.engagementCost}`]: t(
            'engagementMetric.enterPayoutPerEngagement',
        ),
        [`${EngagementId.ENGAGEMENT}_${fieldIdentifiers.engagementMin}`]: t('engagementMetric.enterMinimumEngagement'),
        [`${EngagementId.ENGAGEMENT}_${fieldIdentifiers.engagementMax}`]: t('engagementMetric.enterMaximumEngagement'),
        //view video
        [`view_video_${fieldIdentifiers.engagementToggle}`]: t('engagementMetric.videoView'),
        [`view_video_${fieldIdentifiers.engagementCost}`]: t('engagementMetric.enterPayoutPerView'),
        [`view_video_${fieldIdentifiers.engagementMin}`]: t('engagementMetric.enterMinimumView'),
        [`view_video_${fieldIdentifiers.engagementMax}`]: t('engagementMetric.enterMaximumView'),

        // total watch time
        [`${EngagementId.TOTAL_WATCH_TIME}_${fieldIdentifiers.engagementToggle}`]: t('engagementMetric.totalWatchTime'),
        [`${EngagementId.TOTAL_WATCH_TIME}_${fieldIdentifiers.engagementCost}`]: t(
            'engagementMetric.enterPayoutPerTotalWatchTime',
        ),
        [`${EngagementId.TOTAL_WATCH_TIME}_${fieldIdentifiers.engagementMin}`]: t(
            'engagementMetric.enterPayoutMinimumTotalWatchTime',
        ),
        [`${EngagementId.TOTAL_WATCH_TIME}_${fieldIdentifiers.engagementMax}`]: t(
            'engagementMetric.enterPayoutMaximumTotalWatchTime',
        ),

        // total play time
        [`${EngagementId.TOTAL_PLAY_TIME}_${fieldIdentifiers.engagementToggle}`]: t(
            'submission.campaignReport.metric.totalPlayTime',
        ),
        [`${EngagementId.TOTAL_PLAY_TIME}_${fieldIdentifiers.engagementCost}`]: t(
            'engagementMetric.enterPayoutPerTotalPlayTime',
        ),
        [`${EngagementId.TOTAL_PLAY_TIME}_${fieldIdentifiers.engagementMin}`]: t(
            'engagementMetric.enterPayoutMinimumTotalPlayTime',
        ),
        [`${EngagementId.TOTAL_PLAY_TIME}_${fieldIdentifiers.engagementMax}`]: t(
            'engagementMetric.enterPayoutMaximumTotalPlayTime',
        ),

        // watch full video percentage
        [`${EngagementId.WATCHED_FULL_VIDEO}_${fieldIdentifiers.engagementToggle}`]: t(
            'engagementMetric.watchedFullVideo',
        ),
        [`${EngagementId.WATCHED_FULL_VIDEO}_${fieldIdentifiers.engagementCost}`]: t(
            'engagementMetric.enterPayoutPerWatchedFullVideo',
        ),
        [`${EngagementId.WATCHED_FULL_VIDEO}_${fieldIdentifiers.engagementMin}`]: t(
            'engagementMetric.enterPayoutMinimumWatchedFullVideo',
        ),
        [`${EngagementId.WATCHED_FULL_VIDEO}_${fieldIdentifiers.engagementMax}`]: t(
            'engagementMetric.enterPayoutMaximumWatchedFullVideo',
        ),

        // average watch time
        [`${EngagementId.AVERAGE_WATCH_TIME}_${fieldIdentifiers.engagementToggle}`]: t(
            'engagementMetric.averageWatchTime',
        ),
        [`${EngagementId.AVERAGE_WATCH_TIME}_${fieldIdentifiers.engagementCost}`]: t(
            'engagementMetric.enterPayoutPerAverageWatchTime',
        ),
        [`${EngagementId.AVERAGE_WATCH_TIME}_${fieldIdentifiers.engagementMin}`]: t(
            'engagementMetric.enterPayoutMinimumAverageWatchTime',
        ),
        [`${EngagementId.AVERAGE_WATCH_TIME}_${fieldIdentifiers.engagementMax}`]: t(
            'engagementMetric.enterPayoutMaximumAverageWatchTime',
        ),

        // Campaign template
        [`${CampaignTemplateStep.BRAND_AND_BUDGET}_${fieldIdentifiers.templateBrand}`]: t(
            'campaign.template.brandName.title',
        ),
        [`${CampaignTemplateStep.BRAND_AND_BUDGET}_${fieldIdentifiers.templateBudget}`]: t(
            'campaign.template.yourBudget.title',
        ),
        [`${CampaignTemplateStep.CAMPAIGN_REQUIREMENTS}_${fieldIdentifiers.templatePurchaseNeeds}`]: t(
            'campaign.template.purchaseNeeds.title',
        ),
        [`${CampaignTemplateStep.CAMPAIGN_REQUIREMENTS}_${fieldIdentifiers.templatePlatformPost}`]: t(
            'campaignEditor.setUpCampaign.enterCampaignCategoryHeader',
        ),
        [`${CampaignTemplateStep.CAMPAIGN_REQUIREMENTS}_${fieldIdentifiers.templateRequireCreateContent}`]: t(
            'campaign.template.platformPost.instagramStoryUCG.title',
        ),
        [`${CampaignTemplateStep.CAMPAIGN_REQUIREMENTS}_${fieldIdentifiers.templateReviewApproval}`]: t(
            'campaign.template.credibilityNeed.option.reviews.priority.title',
        ),
        [`${CampaignTemplateStep.CAMPAIGN_REQUIREMENTS}_${fieldIdentifiers.templateReviewImage}`]: t(
            'campaign.template.credibilityNeed.option.reviews.reviewTpye.title',
        ),
        [`${CampaignTemplateStep.TEMPLATE_SELECTION}_${fieldIdentifiers.templateSelection}`]: t(
            'campaign.template.step1.templateSelection.title',
        ),
        [`${CampaignTemplateStep.CAMPAIGN_INFO}_${fieldIdentifiers.templateCampaignName}`]: t(
            'campaign.template.campaignName.title',
        ),
        [`${CampaignTemplateStep.CAMPAIGN_INFO}_${fieldIdentifiers.templateCategories}`]: t(
            'campaign.template.campaignCategory.title',
        ),
        [`${CampaignTemplateStep.CAMPAIGN_INFO}_${fieldIdentifiers.templateBannerImage}`]: t(
            'campaign.template.campaignBanner.title',
        ),
        [`${CampaignTemplateStep.CAMPAIGN_SCHEDULE}_${fieldIdentifiers.templateRegistrationEnable}`]: t(
            'campaign.template.step2.campaignSchedule.toggle.title',
        ),
        [`${CampaignTemplateStep.CAMPAIGN_SCHEDULE}_${fieldIdentifiers.templateStartDate}`]: t(
            'campaign.template.campaignSchedule.title',
        ),
        [`${CampaignTemplateStep.CAMPAIGN_SCHEDULE}_${fieldIdentifiers.templatePayoutDeadline}`]: t(
            'campaignEditor.completeSetup.enterPayoutDeadlineHeader',
        ),
        [`${CampaignTemplateStep.PURCHASE}_${fieldIdentifiers.templateProducts}`]: t(
            'campaign.template.productDetails.title',
        ),
        [`${CampaignTemplateStep.PURCHASE}_${fieldIdentifiers.templateMinimumPurchaseQuantity}`]: t(
            'campaign.template.itemNumber.title',
        ),
        [`${CampaignTemplateStep.PURCHASE}_${fieldIdentifiers.templateExampleMedia}`]: t(
            'campaign.template.productImage.title',
        ),
        [`${CampaignTemplateStep.PURCHASE}_${fieldIdentifiers.templatePurchaseLocations}`]: t(
            'campaign.template.purchaseLocation.title',
        ),
        [`${CampaignTemplateStep.PURCHASE}_${fieldIdentifiers.templateShippingFee}`]: t(
            'campaign.template.shippingFee.title',
        ),
        [`${CampaignTemplateStep.IG_REELS}_${fieldIdentifiers.templateUniqueSellingPoints}`]: t(
            'campaign.template.instagramReelKeyPoints.title',
        ),
        [`${CampaignTemplateStep.IG_REELS}_${fieldIdentifiers.templateExampleMedia}`]: t(
            'campaign.template.instagramReelReferenceImage.title',
        ),
        [`${CampaignTemplateStep.IG_REELS}_${fieldIdentifiers.templateExampleMediaDescription}`]: t(
            'campaign.template.tiktokReferenceCaption.title',
        ),
        [`${CampaignTemplateStep.IG_REELS}_${fieldIdentifiers.templateBrandHandles}`]: t(
            'campaign.template.instagramProfileTag.title',
        ),
        [`${CampaignTemplateStep.IG_REELS}_${fieldIdentifiers.templateCaptionHashtags}`]: t(
            'campaign.template.instagramHashtag.title',
        ),
        [`${CampaignTemplateStep.IG_REELS}_${fieldIdentifiers.templateContentAngle}`]: t(
            'campaign.template.step2.contentAngle.title',
        ),
        [`${CampaignTemplateStep.IG_STORY_REPOST}_${fieldIdentifiers.templateExampleMedia}`]: t(
            'campaign.template.instagramStoryReferenceImage.title',
        ),
        [`${CampaignTemplateStep.IG_STORY_REPOST}_${fieldIdentifiers.templateBrandHandles}`]: t(
            'campaign.template.instagramStoryProfileTag.title',
        ),
        [`${CampaignTemplateStep.IG_STORY_REPOST}_${fieldIdentifiers.templateCaptionHashtags}`]: t(
            'campaign.template.instagramStoryHashtag.title',
        ),
        [`${CampaignTemplateStep.IG_STORY_REPOST}_${fieldIdentifiers.templateStickerLink}`]: t(
            'campaign.template.instagramStoryNonUGC.linkStickers.title',
        ),
        [`${CampaignTemplateStep.IG_STORY_REPOST}_${fieldIdentifiers.templateIsCustomizedStickerText}`]: t(
            'campaign.template.instagramStoryNonUGC.customisedStickerText.title',
        ),
        [`${CampaignTemplateStep.IG_STORY_REPOST}_${fieldIdentifiers.templateCustomizedStickerText}`]: t(
            'campaign.template.instagramStoryNonUGC.customisedStickerTextInput.title',
        ),
        [`${CampaignTemplateStep.TIKTOK_REPOST}_${fieldIdentifiers.templateBrandHandles}`]: t(
            'campaign.template.tiktokProfileTag.title',
        ),
        [`${CampaignTemplateStep.TIKTOK_REPOST}_${fieldIdentifiers.templateCaptionHashtags}`]: t(
            'campaign.template.instagramHashtag.title',
        ),
        [`${CampaignTemplateStep.TIKTOK}_${fieldIdentifiers.templateUniqueSellingPoints}`]: t(
            'campaign.template.tiktokKeyPoints.title',
        ),
        [`${CampaignTemplateStep.TIKTOK}_${fieldIdentifiers.templateExampleMedia}`]: t(
            'campaign.template.tiktokReference.title',
        ),
        [`${CampaignTemplateStep.TIKTOK}_${fieldIdentifiers.templateExampleMediaDescription}`]: t(
            'campaign.template.tiktokReferenceCaption.title',
        ),
        [`${CampaignTemplateStep.TIKTOK}_${fieldIdentifiers.templateBrandHandles}`]: t(
            'campaign.template.tiktokProfileTag.title',
        ),
        [`${CampaignTemplateStep.TIKTOK}_${fieldIdentifiers.templateCaptionHashtags}`]: t(
            'campaign.template.tiktokHashtag.title',
        ),
        [`${CampaignTemplateStep.TIKTOK}_${fieldIdentifiers.templateContentAngle}`]: t(
            'campaign.template.step2.contentAngle.title',
        ),
        [`${CampaignTemplateStep.DOWNLOAD_APP}_${fieldIdentifiers.templateAppName}`]: t(
            'campaign.template.downloadAppName.title',
        ),
        [`${CampaignTemplateStep.DOWNLOAD_APP}_${fieldIdentifiers.templateDownloadLinks}`]: t(
            'campaign.template.downloadAppFrom.title',
        ),
        [`${CampaignTemplateStep.DOWNLOAD_APP_SIGN_UP_WITHOUT_ID}_${fieldIdentifiers.templateAppName}`]: t(
            'campaign.template.downloadAppName.title',
        ),
        [`${CampaignTemplateStep.DOWNLOAD_APP_SIGN_UP_WITHOUT_ID}_${fieldIdentifiers.templateDownloadLinks}`]: t(
            'campaign.template.downloadAppFrom.title',
        ),
        [`${CampaignTemplateStep.IG_POST}_${fieldIdentifiers.templateUniqueSellingPoints}`]: t(
            'campaign.template.instagramPostKeyPoints.title',
        ),
        [`${CampaignTemplateStep.IG_POST}_${fieldIdentifiers.templateExampleMedia}`]: t(
            'campaign.template.instagramPostReference.title',
        ),
        [`${CampaignTemplateStep.IG_POST}_${fieldIdentifiers.templateExampleMediaDescription}`]: t(
            'campaign.template.tiktokReferenceCaption.title',
        ),
        [`${CampaignTemplateStep.IG_POST}_${fieldIdentifiers.templateBrandHandles}`]: t(
            'campaign.template.instagramProfileTag.title',
        ),
        [`${CampaignTemplateStep.IG_POST}_${fieldIdentifiers.templateCaptionHashtags}`]: t(
            'campaign.template.instagramHashtag.title',
        ),
        [`${CampaignTemplateStep.IG_POST}_${fieldIdentifiers.templateContentAngle}`]: t(
            'campaign.template.step2.contentAngle.title',
        ),
        [`${CampaignTemplateStep.IG_STORY_NON_UGC_STICKER_LINK}_${fieldIdentifiers.templateSampleLinks}`]: t(
            'campaign.template.instagramStoryNonUGC.link.title',
        ),
        [`${CampaignTemplateStep.IG_STORY_NON_UGC_STICKER_LINK}_${fieldIdentifiers.templateUniqueSellingPoints}`]: t(
            'campaign.template.instagramStoryKeyPoints.title',
        ),
        [`${CampaignTemplateStep.IG_STORY_NON_UGC_STICKER_LINK}_${fieldIdentifiers.templateExampleMedia}`]: t(
            'campaign.template.instagramStoryReferenceImage.title',
        ),
        [`${CampaignTemplateStep.IG_STORY_NON_UGC_STICKER_LINK}_${fieldIdentifiers.templateStickerLink}`]: t(
            'campaign.template.instagramStoryNonUGC.linkStickers.title',
        ),
        [`${CampaignTemplateStep.IG_STORY_NON_UGC_STICKER_LINK}_${fieldIdentifiers.templateIsCustomizedStickerText}`]:
            t('campaign.template.instagramStoryNonUGC.customisedStickerText.title'),
        [`${CampaignTemplateStep.IG_STORY_NON_UGC_STICKER_LINK}_${fieldIdentifiers.templateCustomizedStickerText}`]: t(
            'campaign.template.instagramStoryNonUGC.customisedStickerTextInput.title',
        ),
        [`${CampaignTemplateStep.IG_STORY_NON_UGC_STICKER_LINK}_${fieldIdentifiers.templateBrandHandles}`]: t(
            'campaign.template.instagramStoryProfileTag.title',
        ),
        [`${CampaignTemplateStep.IG_STORY_NON_UGC_STICKER_LINK}_${fieldIdentifiers.templateCaptionHashtags}`]: t(
            'campaign.template.instagramStoryHashtag.title',
        ),
        [`${CampaignTemplateStep.REVIEW_TEXT_IMAGE_APPROVAL_PURCHASE}_${fieldIdentifiers.templateUniqueSellingPoints}`]:
            t('campaign.template.step2.reviewTextImageWithApprovalTaskKeyPoints.title'),
        [`${CampaignTemplateStep.REVIEW_TEXT_IMAGE_APPROVAL_PURCHASE}_${fieldIdentifiers.templateReviewPlatform}`]: t(
            'campaign.template.step2.reviewTextImageWithApprovalTaskPlatformName.title',
        ),
        [`${CampaignTemplateStep.REVIEW_TEXT_IMAGE_APPROVAL_PURCHASE}_${fieldIdentifiers.templateReviewLink}`]: t(
            'campaign.template.step2.reviewTextImageWithApprovalTaskPlatformURL.title',
        ),
        [`${CampaignTemplateStep.REVIEW_TEXT_IMAGE_APPROVAL}_${fieldIdentifiers.templateUniqueSellingPoints}`]: t(
            'campaign.template.step2.reviewTextImageWithApprovalTaskKeyPoints.title',
        ),
        [`${CampaignTemplateStep.REVIEW_TEXT_IMAGE_APPROVAL}_${fieldIdentifiers.templateReviewPlatform}`]: t(
            'campaign.template.step2.reviewTextImageWithApprovalTaskPlatformName.title',
        ),
        [`${CampaignTemplateStep.REVIEW_TEXT_IMAGE_APPROVAL}_${fieldIdentifiers.templateReviewLink}`]: t(
            'campaign.template.step2.reviewTextImageWithApprovalTaskPlatformURL.title',
        ),
        [`${CampaignTemplateStep.PAYMENT_CONFIRMATION}_${fieldIdentifiers.templateCustomiseToggle}`]: t(
            'campaign.template.step3.paymentConfirmation.customiseToggle.title',
        ),
        [`${CampaignTemplateStep.PAYMENT_CONFIRMATION}_${fieldIdentifiers.templateBudget}`]: t(
            'campaign.template.yourBudget.title',
        ),
        [`${CampaignTemplateStep.IG_STORY_UGC}_${fieldIdentifiers.templateUniqueSellingPoints}`]: t(
            'campaign.template.instagramStoryKeyPoints.title',
        ),
        [`${CampaignTemplateStep.IG_STORY_UGC}_${fieldIdentifiers.templateExampleMedia}`]: t(
            'campaign.template.instagramStoryReferenceImage.title',
        ),
        [`${CampaignTemplateStep.IG_STORY_UGC}_${fieldIdentifiers.templateBrandHandles}`]: t(
            'campaign.template.instagramStoryProfileTag.title',
        ),
        [`${CampaignTemplateStep.IG_STORY_UGC}_${fieldIdentifiers.templateCaptionHashtags}`]: t(
            'campaign.template.instagramStoryHashtag.title',
        ),
        [`${CampaignTemplateStep.IG_STORY_UGC}_${fieldIdentifiers.templateContentAngle}`]: t(
            'campaign.template.step2.contentAngle.title',
        ),
        [`${CampaignTemplateStep.IG_STORY_UGC}_${fieldIdentifiers.templateStickerLink}`]: t(
            'campaign.template.instagramStoryNonUGC.linkStickers.title',
        ),
        [`${CampaignTemplateStep.IG_STORY_UGC}_${fieldIdentifiers.templateIsCustomizedStickerText}`]: t(
            'campaign.template.instagramStoryNonUGC.customisedStickerText.title',
        ),
        [`${CampaignTemplateStep.IG_STORY_UGC}_${fieldIdentifiers.templateCustomizedStickerText}`]: t(
            'campaign.template.instagramStoryNonUGC.customisedStickerTextInput.title',
        ),
        [`${CampaignTemplateStep.REVIEW_TEXT_APPROVAL}_${fieldIdentifiers.templateUniqueSellingPoints}`]: t(
            'campaign.template.step2.reviewTextImageWithApprovalTaskKeyPoints.title',
        ),
        [`${CampaignTemplateStep.REVIEW_TEXT_APPROVAL}_${fieldIdentifiers.templateReviewPlatform}`]: t(
            'campaign.template.step2.reviewTextImageWithApprovalTaskPlatformName.title',
        ),
        [`${CampaignTemplateStep.REVIEW_TEXT_APPROVAL}_${fieldIdentifiers.templateReviewLink}`]: t(
            'campaign.template.step2.reviewTextImageWithApprovalTaskPlatformURL.title',
        ),
        [`${CampaignTemplateStep.REVIEW_TEXT_NO_APPROVAL}_${fieldIdentifiers.templateUniqueSellingPoints}`]: t(
            'campaign.template.step2.reviewTextImageWithApprovalTaskKeyPoints.title',
        ),
        [`${CampaignTemplateStep.REVIEW_TEXT_NO_APPROVAL}_${fieldIdentifiers.templateReviewPlatform}`]: t(
            'campaign.template.step2.reviewTextImageWithApprovalTaskPlatformName.title',
        ),
        [`${CampaignTemplateStep.REVIEW_TEXT_NO_APPROVAL}_${fieldIdentifiers.templateReviewLink}`]: t(
            'campaign.template.step2.reviewTextImageWithApprovalTaskPlatformURL.title',
        ),
        [`${CampaignTemplateStep.REVIEW_TEXT_NO_APPROVAL_PURCHASE}_${fieldIdentifiers.templateUniqueSellingPoints}`]: t(
            'campaign.template.step2.reviewTextImageWithApprovalTaskKeyPoints.title',
        ),
        [`${CampaignTemplateStep.REVIEW_TEXT_NO_APPROVAL_PURCHASE}_${fieldIdentifiers.templateReviewPlatform}`]: t(
            'campaign.template.step2.reviewTextImageWithApprovalTaskPlatformName.title',
        ),
        [`${CampaignTemplateStep.REVIEW_TEXT_NO_APPROVAL_PURCHASE}_${fieldIdentifiers.templateReviewLink}`]: t(
            'campaign.template.step2.reviewTextImageWithApprovalTaskPlatformURL.title',
        ),
        [`${CampaignTemplateStep.FOLLOW}_${fieldIdentifiers.templateFollowPlatform}`]: t(
            'campaign.template.step2.followTask.platform.title',
        ),
        [`${CampaignTemplateStep.FOLLOW}_${fieldIdentifiers.templateFollowPlatformLink}`]: t(
            'campaign.template.step2.followTask.URL.title',
        ),
        [`${CampaignTemplateStep.VIDEO_CREATION}_${fieldIdentifiers.templateNumberOfVideo}`]: t(
            'campaign.template.step2.videoCreation.numberOfVideo.title',
        ),
        [`${CampaignTemplateStep.VIDEO_CREATION}_${fieldIdentifiers.templateUniqueSellingPoints}`]: t(
            'campaign.template.step2.videoCreation.keyPoints.title',
        ),
        [`${CampaignTemplateStep.VIDEO_CREATION}_${fieldIdentifiers.templateMandatoryGuidelines}`]: t(
            'campaign.template.step2.videoCreation.videoGuidelines.title',
        ),
        [`${CampaignTemplateStep.VIDEO_CREATION}_${fieldIdentifiers.templateExampleMedia}`]: t(
            'campaign.template.step2.videoCreation.sampleVideo.title',
        ),
        [`${CampaignTemplateStep.PHOTO_CREATION}_${fieldIdentifiers.templateUniqueSellingPoints}`]: t(
            'campaign.template.step2.photoCreation.keyPoints.title',
        ),
        [`${CampaignTemplateStep.PHOTO_CREATION}_${fieldIdentifiers.templateMandatoryGuidelines}`]: t(
            'campaign.template.step2.photoCreation.photoGuidelines.title',
        ),
        [`${CampaignTemplateStep.PHOTO_CREATION}_${fieldIdentifiers.templateExampleMedia}`]: t(
            'campaign.template.step2.photoCreation.photoReference.title',
        ),
    }
    return titlesList[identifier] ? titlesList[identifier] : ''
}

/**
 *
 * Hook to get comment card title
 *
 * @param identifier
 * @returns corresponding field title
 */
export const useGetCommentCardTitle = (identifier?: string, typeId?: string) => {
    const [t] = useNewTranslation()
    const typeIdSnakeCase: string = typeId ? camelCaseToSnakeCase(typeId) : ''
    const engagementIds: string[] = EngagementIds
    const campaignTemplateStepValues: string[] = CampaignTemplateStepValues

    if (identifier && engagementIds.includes(typeIdSnakeCase)) {
        return getCardTitle(`${typeIdSnakeCase}_${identifier}`, t)
    } else if (identifier && typeId && campaignTemplateStepValues.includes(typeId)) {
        return getCardTitle(`${typeId}_${identifier}`, t)
    } else if (identifier) {
        return getCardTitle(identifier, t)
    } else {
        return ''
    }
}

const getLocationTitle = () => {
    const cityTypes = useSelector(selectCityTypesByEditingCampaignCountry)
    const rank1CityType = cityTypes.find(city => city.rank === 1)
    const rank2CityType = cityTypes.find(city => city.rank === 2)
    const rank1Title = useLocationTranslation(rank1CityType?.id_name)
    const rank2Title = useLocationTranslation(rank2CityType?.id_name)

    return {
        rank1Title,
        rank2Title,
    }
}

export const useToggleCampaignEditorDrawer = () => {
    const location = useLocation()
    const history = useHistory()

    const updateToggleCampaignEditorDrawer = (drawer: 'comments' | 'preview') => {
        const searchParams = new URLSearchParams(location.search)
        const currentDrawer = searchParams.get('drawer')
        if (currentDrawer === drawer) {
            searchParams.delete('drawer')
        } else {
            searchParams.set('drawer', drawer)
        }
        history.replace({
            pathname: location.pathname,
            search: searchParams.toString(),
        })
    }

    return updateToggleCampaignEditorDrawer
}

/**
 *
 * Hook to set route with identifier
 *
 * @param identifier
 * @returns update route with identifier
 */

interface updateRouteParams {
    identifier?: string | null
    threadId?: string | null
    highlightSelected?: boolean
    taskUuid?: string | null
    section?: TaskSections | null
    stepUuid?: string | null
}
export const useSetIdentifierRoute = () => {
    const location = useLocation()
    const history = useHistory()
    const updateRoute = (args: updateRouteParams | null) => {
        const searchParams = new URLSearchParams(location.search)
        const qs = args ? args : {}
        const { identifier, threadId, highlightSelected, taskUuid, section, stepUuid } = qs
        if (identifier && identifier !== null) {
            searchParams.set('selectedIdentifier', identifier)
        }
        if (threadId) {
            searchParams.set('selectedThread', threadId)
        }
        if (highlightSelected) {
            searchParams.set('highlightSelected', highlightSelected.toString())
        }
        if (taskUuid) {
            searchParams.set('taskUuid', taskUuid)
        }
        if (section) {
            searchParams.set('section', section)
        }
        if (stepUuid) {
            searchParams.set('stepUuid', stepUuid)
        }
        if (!identifier || identifier === null) {
            searchParams.delete('selectedIdentifier')
        }
        if (!threadId) {
            searchParams.delete('selectedThread')
        }
        if (!highlightSelected) {
            searchParams.delete('highlightSelected')
        }
        if (taskUuid === null) {
            searchParams.delete('taskUuid')
        }
        if (section === null) {
            searchParams.delete('section')
        }
        if (stepUuid === null) {
            searchParams.delete('stepUuid')
        }
        if (args === null) {
            history.replace({
                pathname: location.pathname,
                search: searchParams.toString(),
            })
        } else {
            history.push({
                pathname: location.pathname,
                search: searchParams.toString(),
            })
        }
    }
    return [updateRoute]
}

/**
 *
 * Hook to get tasks collection relative identifiers
 * @returns structure of each tabs identifiers
 */

// const dynamicFields = ['extra_data', 'blocks']

export const TabTypes = {
    SETUP: 'setup',
    STEPS: 'steps',
    SCHEDULE: 'schedule',
    PAYOUT: 'payout',
} as const

type PayoutStateKeys = keyof PayoutState
const scheduleIdentifiers = [
    fieldIdentifiers.start,
    fieldIdentifiers.end,
    fieldIdentifiers.schedulePostToggle,
    fieldIdentifiers.invitationExpiry,
] as string[]

export const dynamicFields = [
    fieldIdentifiers.captionRequirementDos,
    fieldIdentifiers.captionRequirementDonts,
    fieldIdentifiers.captionRequirementHashtags,
    fieldIdentifiers.profileTags,
    fieldIdentifiers.howToBuyTitle,
    fieldIdentifiers.eventInformation,
    fieldIdentifiers.thingsToNote,
    fieldIdentifiers.photoRequirementDonts,
    fieldIdentifiers.photoRequirementDos,
] as string[]

const dynamicFieldsKeys = [
    'caption_requirement_donts',
    'caption_requirement_dos',
    'caption_requirement_hashtags',
    'media_requirement_donts',
    'media_requirement_dos',
    'event_information',
    'how_to_buy',
    'things_to_note',
]

const extractUUIDs = (data: TaskState) => {
    const uuids: string[] = []
    if (data && data?.steps) {
        data.steps.forEach((step: StepState) => {
            dynamicFieldsKeys.forEach(key => {
                if (step.data[key]) {
                    step.data[key].forEach((item: Record<string, any>) => {
                        if (item.uuid) {
                            uuids.push(item.uuid)
                        }
                    })
                }
            })
        })
    }

    return uuids
}

type NestedObject = { [key: string]: any }

const getUUIDs = (data: NestedObject | NestedObject[]): string[] => {
    const uuids: string[] = []

    function scan(item: any) {
        if (typeof item !== 'object' || item === null) {
            return
        }

        if (Array.isArray(item)) {
            item.forEach(scan)
        } else {
            Object.entries(item).forEach(([key, value]) => {
                if (key === 'uuid' && typeof value === 'string') {
                    uuids.push(value)
                } else {
                    scan(value)
                }
            })
        }
    }

    scan(data)
    return uuids
}

export const useIdentifiersMap = () => {
    const editingCampaign = useSelector(selectEditingCampaign)
    const campaign = editingCampaign
    const campaignUuid = campaign ? campaign.uuid : null
    const campaignComments = useSelector(selectCampaignComments(campaignUuid))
    const registrationForm = useSelector(selectRegistrationForm)
    const templateTasks = useSelector(selectCampaignTasks)
    const tasks = useSelector(selectTasks)
    const taskComments = getSectionComments(campaignComments, tasksIdentifiers)
    const publishComments = getSectionComments(campaignComments, publishIdentifiers)
    const registrationComments = getSectionComments(campaignComments, registrationIdentifiers)
    const templateSelectorComments = getSectionComments(campaignComments, templateSelectorIdentifiers)
    const templateDetailComments = getSectionComments(campaignComments, templateDetailIdentifiers)
    const [t] = useNewTranslation()

    const registrationVerifiedComments = useMemo(() => {
        if (!registrationComments.length) return []

        return registrationComments.map(({ identifier, ...rest }) => ({
            ...rest,
            identifier,
            labelPrefix: identifier.includes('template')
                ? t('campaignEditor.comment.transferToCampaignEditorComment.indicator')
                : '',
        }))
    }, [registrationComments])

    const tasksIdentifierMap = useMemo(() => {
        const dataMap: Record<string, { taskId: string; tab: string }> = {}
        const activeDynamicFieldIds: string[] = []
        if (tasks) {
            tasks.forEach(task => {
                const taskDynamicIds = extractUUIDs(task)
                if (taskDynamicIds.length > 0) {
                    taskDynamicIds.forEach(id => {
                        activeDynamicFieldIds.push(id)
                    })
                }
            })
        }

        if (taskComments.length > 0) {
            const commentIds = taskComments.map(comment => comment.identifier)
            commentIds.forEach((commentId: string) => {
                if (registrationForm) {
                    if (commentId.includes('setup') && commentId.includes(registrationForm.uuid)) {
                        dataMap[commentId] = { taskId: 'registration', tab: TabTypes.SETUP }
                    }
                    if (commentId.includes('registration') && commentId.includes(registrationForm.uuid)) {
                        dataMap[commentId] = { taskId: 'registration', tab: TabTypes.SCHEDULE }
                    }
                    if (commentId === fieldIdentifiers.registrationFieldsHeader) {
                        dataMap[commentId] = { taskId: 'registration', tab: TabTypes.STEPS }
                    }
                    const registrationStepsIds = registrationForm?.blocks.map(block => block.uuid)
                    const blockWithOptions = registrationForm?.blocks.filter(block => block.options.length > 0)
                    if (blockWithOptions.length > 0) {
                        blockWithOptions.forEach((block: any) => {
                            block.options.forEach((option: any) => registrationStepsIds.push(option.uuid))
                        })
                    }
                    registrationStepsIds.forEach(id => {
                        if (commentId.includes(id)) {
                            dataMap[commentId] = { taskId: 'registration', tab: TabTypes.STEPS }
                        }
                    })
                }

                if (tasks) {
                    tasks.forEach((task: TaskState) => {
                        const taskUuid = task.uuid
                        const fieldIdentifier = commentId.substring(commentId.lastIndexOf('_') + 1)

                        const taskStepIds: string[] = []

                        task.steps.map(step => {
                            taskStepIds.push(step.uuid)
                            if (Array.isArray(step.data?.extra_data) && step.data?.extra_data.length > 0) {
                                step.data.extra_data.forEach(extraData => {
                                    if (extraData?.uuid) {
                                        taskStepIds.push(extraData.uuid)
                                    }
                                })
                            }
                            if (Array.isArray(step.data?.blocks) && step.data?.blocks.length > 0) {
                                step.data?.blocks.forEach(block => {
                                    if (block?.uuid) {
                                        taskStepIds.push(block.uuid)
                                    }
                                })
                            }
                        })

                        if (taskStepIds.length > 0) {
                            taskStepIds.forEach(id => {
                                if (commentId.includes(id)) {
                                    const commentIdArray = commentId.split('_')
                                    //check if the comment corresponding field still exist
                                    if (commentIdArray.length === 3 && dynamicFields.includes(fieldIdentifier)) {
                                        const isIdentifierActive = activeDynamicFieldIds.includes(commentIdArray[0])
                                        if (isIdentifierActive) {
                                            dataMap[commentId] = { taskId: taskUuid, tab: TabTypes.STEPS }
                                        }
                                    } else {
                                        dataMap[commentId] = { taskId: taskUuid, tab: TabTypes.STEPS }
                                    }
                                }
                            })
                        }
                        if (commentId.includes(taskUuid)) {
                            if (commentId.includes('setup')) {
                                dataMap[commentId] = { taskId: taskUuid, tab: TabTypes.SETUP }
                            }
                            //base Payout & engagement toggle
                            if (
                                fieldIdentifier === fieldIdentifiers.payoutBase ||
                                fieldIdentifier === fieldIdentifiers.engagementToggle
                            ) {
                                dataMap[commentId] = { taskId: taskUuid, tab: TabTypes.PAYOUT }
                            }
                            // schedule
                            if (scheduleIdentifiers.includes(fieldIdentifier)) {
                                dataMap[commentId] = { taskId: taskUuid, tab: TabTypes.SCHEDULE }
                            }
                        }

                        if (task.payouts.extras) {
                            Object.keys(task.payouts.extras).forEach(key => {
                                if (commentId.includes(key)) {
                                    dataMap[commentId] = { taskId: taskUuid, tab: TabTypes.PAYOUT }
                                }
                            })
                        }
                        const engagementPayoutIds: PayoutStateKeys[] = Object.keys(task.payouts).filter(
                            (key: string) => {
                                if (key !== 'extras' && key !== 'base') {
                                    return true
                                }
                                return false
                            },
                        ) as PayoutStateKeys[]

                        engagementPayoutIds.forEach(key => {
                            const engagementPayout = task.payouts[key] as Record<string, string | number>
                            if (engagementPayout) {
                                const engagementId: string = String(engagementPayout?.uuid)
                                if (engagementId && commentId.includes(engagementId)) {
                                    dataMap[commentId] = { taskId: taskUuid, tab: TabTypes.PAYOUT }
                                }
                            }
                        })
                    })
                }
            })
        }
        return dataMap
    }, [registrationForm, tasks, taskComments])

    const tasksVerifiedComments = useMemo(() => {
        let comments: { identifier: string; isFieldExist: boolean; threads: ThreadCommentState[] }[] = []
        if (taskComments.length > 0) {
            comments = taskComments.map(comment => {
                comment.isFieldExist = tasksIdentifierMap.hasOwnProperty(comment.identifier)
                return comment
            })
        }
        return comments
    }, [tasksIdentifierMap, taskComments])

    const publishVerifiedComments = useMemo(() => {
        let comments: { identifier: string; isFieldExist: boolean; threads: ThreadCommentState[] }[] = []
        if (publishComments.length > 0) {
            const publishFieldIds: string[] = [
                fieldIdentifiers.payoutDeadline,
                fieldIdentifiers.campaignBanner,
                fieldIdentifiers.campaignDescription,
                fieldIdentifiers.campaignRequirement,
            ]
            if (editingCampaign?.requirements) {
                editingCampaign?.requirements.forEach(requirement => {
                    if (requirement?.properties?.locations) {
                        requirement.properties.locations.forEach(location => {
                            Object.values(location).forEach(value => {
                                if (value?.uuid) {
                                    publishFieldIds.push(value?.uuid)
                                }
                            })
                        })
                    } else if (requirement?.properties?.followers) {
                        requirement.properties.followers?.forEach(value => {
                            if (value?.uuid) {
                                publishFieldIds.push(value?.uuid)
                            }
                        })
                    } else {
                        if (requirement?.uuid) {
                            publishFieldIds.push(requirement?.uuid)
                        }
                    }
                })
            }
            comments = publishComments.map(comment => {
                const commentFieldIdentifier = comment.identifier.split('_')[0]
                comment.isFieldExist = publishFieldIds.includes(commentFieldIdentifier)
                return comment
            })
        }
        return comments
    }, [editingCampaign, publishComments])

    const unresolvedFieldIdentifiers = useMemo(() => {
        const unResolvedFields: Record<string, any> = {}
        taskComments.forEach(item => {
            if (item.isFieldExist && item.threads.some(thread => thread.resolved_at === null)) {
                const id = tasksIdentifierMap[item.identifier].taskId
                const tab = tasksIdentifierMap[item.identifier].tab
                const initObject = unResolvedFields[id] ? { ...unResolvedFields[id] } : { total: 0 }
                const total = unResolvedFields[id] ? unResolvedFields[id]['total'] + 1 : 1
                const tabNumbs = unResolvedFields[id] && unResolvedFields[id][tab] ? unResolvedFields[id][tab] + 1 : 1

                initObject[tab] = tabNumbs
                unResolvedFields[id] = { ...initObject, total: total }
            }
        })
        return unResolvedFields
    }, [taskComments, tasksIdentifierMap])

    const campaignTemplateVerifiedComments = useMemo(() => {
        const templateFieldUuids = getUUIDs(templateTasks)
        const validIdentifiers = [...templateFieldUuids, ...CampaignTemplateStepValues]

        const processComments = (
            comments: { identifier: string; isFieldExist: boolean; threads: ThreadCommentState[] }[],
        ) =>
            comments.map(comment => ({
                ...comment,
                isFieldExist: validIdentifiers.includes(comment.identifier.split('_')[0]),
            }))

        const groupedComments = {
            templateSelector: processComments(templateSelectorComments),
            templateDetail: processComments(templateDetailComments),
        }

        return groupedComments
    }, [templateDetailComments, templateSelectorComments])

    const campaignTemplateIdentifierMap = useMemo(() => {
        const templateFieldUuids = getUUIDs(templateTasks)
        const verifiedCommentsMap = Object.values(campaignTemplateVerifiedComments).flatMap(comment => comment)

        return verifiedCommentsMap
            .filter(comment => comment.isFieldExist)
            .reduce((acc: Record<string, { stepKey: string }>, comment) => {
                const [firstString, secondString] = comment.identifier.split('_')
                const matchingKey = CampaignTemplateStepValues.find(key => {
                    return templateFieldUuids.includes(firstString) ? secondString === key : firstString === key
                })

                if (matchingKey) {
                    acc[comment.identifier] = { stepKey: camelCaseToSnakeCase(matchingKey) }
                }
                return acc
            }, {})
    }, [campaignTemplateVerifiedComments])

    const unresolvedTemplateFieldIdentifiers = useMemo(() => {
        const unResolvedFields: Record<string, { total: number }> = {}
        const verifiedCommentsMap = Object.values(campaignTemplateVerifiedComments).flatMap(comment => comment)

        verifiedCommentsMap.forEach(item => {
            if (item.isFieldExist && item.threads.some(thread => thread.resolved_at === null)) {
                const id =
                    campaignTemplateIdentifierMap[item.identifier]?.stepKey ||
                    campaignTemplateIdentifierMap[item.identifier.split('_').slice(1).join('_')]?.stepKey
                if (id) {
                    unResolvedFields[id] = {
                        total: (unResolvedFields[id]?.total || 0) + 1,
                    }
                }
            }
        })

        return unResolvedFields
    }, [templateDetailComments, templateSelectorComments, campaignTemplateIdentifierMap])

    return {
        tasksIdentifierMap,
        tasksVerifiedComments,
        publishVerifiedComments,
        registrationVerifiedComments,
        campaignTemplateIdentifierMap,
        campaignTemplateVerifiedComments,
        unresolvedFieldIdentifiers,
        unresolvedTemplateFieldIdentifiers,
    }
}

export const useCommentCount = () => {
    const { tasksIdentifierMap, unresolvedFieldIdentifiers } = useIdentifiersMap()
    const commentCount = useCallback(
        (taskUuid: string, section?: string) => {
            const taskCounts = unresolvedFieldIdentifiers[taskUuid]
            return taskCounts ? (section ? taskCounts[section] : taskCounts?.total) : 0
        },
        [tasksIdentifierMap, unresolvedFieldIdentifiers],
    )
    return commentCount
}
